import React, { useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Select,
  useDisclosure,
  Stack,
  HStack,
  IconButton,
  Heading,
  Icon,
  Tooltip,
  SimpleGrid,
  VStack,
} from "@chakra-ui/react";
import { SearchIcon, CopyIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { FaShoppingCart, FaPlus, FaEye, FaSync, FaStore, FaBoxOpen, FaExclamationTriangle } from "react-icons/fa";
import { useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";

// سرویس‌های واقعی و هوک‌ها
import { getOrders, getOrder } from "../services/orderService";
import { getGateways } from "../services/gatewayService";
import { usePersistedPagination } from "../hooks/usePersistedPagination";
import CustomModal from "../components/Modal";
import ManualOrderForm from "../components/forms/ManualOrderForm";

const SOURCE_LABELS = {
  basalam: "باسلام",
  digikala: "دیجی‌کالا",
  custom: "سایت شخصی",
  manual: "دستی",
};

const SOURCE_COLORS = {
  basalam: "orange",
  digikala: "red",
  custom: "purple",
  manual: "teal",
};

function formatDate(dateString) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("fa-IR", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export default function Orders() {
  // 🎨 متغیرهای رنگی (SaaS Design)
  const bgPage = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const theadBg = useColorModeValue("gray.50", "gray.700");
  const filtersBarBg = useColorModeValue("white", "gray.800");
  const paginationBg = useColorModeValue("white", "gray.800");
  const modalHeaderBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const stockSyncOkBg = useColorModeValue("green.50", "green.900");
  const stockSyncFailBg = useColorModeValue("red.50", "red.900");
  const stockSyncUnknownBg = useColorModeValue("gray.50", "gray.700");
  const failureBoxBg = useColorModeValue("white", "gray.800");

  const commonInputProps = {
    bg: useColorModeValue("gray.50", "gray.900"),
    border: "none",
    borderRadius: "xl",
    _focus: { ring: 2, ringColor: "blue.400" },
  };

  const queryClient = useQueryClient();

  const { page: currentPage, setPage: setCurrentPage } = usePersistedPagination("ordersTable", {
    defaultPage: 1,
    defaultLimit: 10,
  });
  
  const [gatewayFilter, setGatewayFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [syncFilter, setSyncFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isManualOpen, onOpen: onManualOpen, onClose: onManualClose } = useDisclosure();

  // Queries
  const { data: gatewaysData } = useQuery(["gateways"], getGateways);
  const gateways = gatewaysData?.data || [];

  const {
    data: ordersData,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["orders", currentPage, gatewayFilter, sourceFilter, statusFilter, syncFilter, searchTerm],
    () =>
      getOrders({
        page: currentPage,
        limit: 10,
        ...(gatewayFilter && { gateway: gatewayFilter }),
        ...(sourceFilter && { source: sourceFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(syncFilter && { stockSyncOk: syncFilter }),
        ...(searchTerm && { search: searchTerm }),
      }),
    { keepPreviousData: true, staleTime: 30 * 1000 }
  );

  const { data: orderDetailData, isLoading: detailLoading } = useQuery(
    ["order", selectedOrderId],
    () => getOrder(selectedOrderId),
    { enabled: Boolean(selectedOrderId && isOpen), staleTime: 10 * 1000 }
  );

  const orders = ordersData?.data || [];
  const pagination = ordersData?.pagination || {};
  const selectedOrder = orderDetailData?.data || null;

  // Helpers
  const statusBadge = (status) => {
    if (status === "processed") return <Badge colorScheme="green" px={2} py={0.5} rounded="md">پردازش شده</Badge>;
    if (status === "failed") return <Badge colorScheme="red" px={2} py={0.5} rounded="md">خطا</Badge>;
    return <Badge colorScheme="blue" px={2} py={0.5} rounded="md">دریافت شده</Badge>;
  };

  /** null/undefined یعنی «اندازه‌گیری نشده» نه «ناموفق» — سفارش‌های وبهوک دیجی‌کالا
   *  همگام‌سازی را در صف می‌گذارند و نتیجه روی سند سفارش ثبت نمی‌شود */
  const syncState = (stockSync) => {
    if (!stockSync || stockSync.ok === null || stockSync.ok === undefined) return "unknown";
    return stockSync.ok ? "ok" : "failed";
  };

  const syncBadge = (stockSync) => {
    if (syncState(stockSync) === "unknown") {
      return <Badge colorScheme="gray" variant="outline" rounded="md" px={2}>نامشخص</Badge>;
    }
    if (stockSync.ok) return <Badge colorScheme="green" rounded="md" px={2} py={0.5}><Icon as={FaSync} mr={1} mb={-0.5}/>موفق</Badge>;
    const count = Array.isArray(stockSync.failures) ? stockSync.failures.length : 0;
    return (
      <Badge colorScheme="red" rounded="md" px={2} py={0.5}>
        <Icon as={FaExclamationTriangle} mr={1} mb={-0.5}/> ناموفق {count ? `(${count})` : ""}
      </Badge>
    );
  };

  const orderItemsSummary = (order) => {
    const items = Array.isArray(order?.items) ? order.items : [];
    const itemsCount = items.length;
    const totalQty = items.reduce((sum, it) => sum + (parseInt(it?.quantity, 10) || 0), 0);
    return { itemsCount, totalQty };
  };

  const copyToClipboard = async (text) => {
    const t = String(text || "");
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t);
      toast.success("در کلیپ‌بورد کپی شد");
    } catch {
      toast.error("کپی ناموفق بود");
    }
  };

  const detailFailures = useMemo(() => {
    const failures = selectedOrder?.stockSync?.failures;
    return Array.isArray(failures) ? failures : [];
  }, [selectedOrder]);

  const handleViewDetail = (orderId) => {
    setSelectedOrderId(orderId);
    onOpen();
  };

  const handleClose = () => {
    onClose();
    setSelectedOrderId(null);
  };

  // Loading & Error States
  if (isLoading) {
    return (
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} align="center" justify="center">
        <Box position="relative" mb={6}>
          <Spinner size="xl" thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" w="60px" h="60px" />
          <Icon as={FaShoppingCart} position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" color="blue.500" />
        </Box>
        <Text fontSize="lg" fontWeight="bold" color={textColor} animation="pulse">در حال دریافت لیست سفارشات...</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} p={8}>
        <Alert status="error" borderRadius="2xl" boxShadow="sm" p={6}>
          <AlertIcon boxSize="24px" />
          <Box ml={4}>
            <AlertTitle fontSize="lg" mb={2}>خطا در بارگذاری داده‌ها!</AlertTitle>
            <AlertDescription color="gray.600">{error.message || "خطا در ارتباط با سرور"}</AlertDescription>
          </Box>
        </Alert>
        <Button mt={6} colorScheme="blue" alignSelf="start" onClick={() => refetch()}>تلاش مجدد</Button>
      </Flex>
    );
  }

  return (
    <>
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} pb={10}>
        <Box maxW="100%" mx="auto" w="full" px={{ base: 4, md: 8 }} py={8}>
          
          {/* هدر و اکشن‌ها */}
          <Flex justify="space-between" align={{ base: "start", lg: "flex-end" }} mb={8} flexDir={{ base: "column", lg: "row" }} gap={6}>
            <Box>
              <Heading size="lg" color={textColor} letterSpacing="tight" mb={2} display="flex" alignItems="center" gap={3}>
                <Flex w="40px" h="40px" borderRadius="xl" bgGradient="linear(to-br, blue.400, cyan.400)" color="white" align="center" justify="center" shadow="sm">
                  <Icon as={FaShoppingCart} boxSize={5} />
                </Flex>
                مدیریت سفارشات
              </Heading>
              <Text fontSize="sm" color={mutedColor}>
                مشاهده و رهگیری سفارش‌های ثبت‌شده از تمام کانال‌های فروش و وضعیت موجودی آن‌ها
              </Text>
            </Box>

            <Button
              leftIcon={<FaPlus />}
              bgGradient="linear(to-r, blue.500, cyan.400)"
              color="white"
              borderRadius="2xl"
              h="50px"
              px={6}
              fontSize="md"
              fontWeight="bold"
              boxShadow="0 10px 20px -5px rgba(59,130,246,0.4)"
              _hover={{ bgGradient: 'linear(to-r, blue.600, cyan.500)', transform: 'translateY(-2px)' }}
              onClick={onManualOpen}
            >
              ثبت سفارش دستی
            </Button>
          </Flex>

          {/* بدنه اصلی (جدول و فیلترها) */}
          <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm" overflow="hidden">
            
            {/* نوار فیلترها */}
            <Box p={4} borderBottomWidth="1px" borderColor={borderColor} bg={filtersBarBg}>
              <Flex gap={3} flexWrap="wrap" align="center">
                <InputGroup size="md" w={{ base: "full", md: "250px" }}>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    {...commonInputProps}
                    placeholder="جستجو با SKU یا نام..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  />
                </InputGroup>
                
                <Select {...commonInputProps} size="md" w={{ base: "full", sm: "auto" }} placeholder="همهٔ درگاه‌ها" value={gatewayFilter} onChange={(e) => { setGatewayFilter(e.target.value); setCurrentPage(1); }}>
                  {gateways.map((g) => <option key={g._id} value={g._id}>{g.name}</option>)}
                </Select>

                <Select {...commonInputProps} size="md" w={{ base: "full", sm: "auto" }} placeholder="همهٔ منابع" value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="digikala">دیجی‌کالا</option>
                  <option value="basalam">باسلام</option>
                  <option value="custom">سایت شخصی</option>
                  <option value="manual">دستی</option>
                </Select>

                <Select {...commonInputProps} size="md" w={{ base: "full", sm: "auto" }} placeholder="همهٔ وضعیت‌ها" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="received">دریافت شد</option>
                  <option value="processed">پردازش شد</option>
                  <option value="failed">خطا</option>
                </Select>

                <Select {...commonInputProps} size="md" w={{ base: "full", sm: "auto" }} placeholder="وضعیت موجودی (همه)" value={syncFilter} onChange={(e) => { setSyncFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="true">سینک موفق</option>
                  <option value="false">سینک ناموفق</option>
                  <option value="unknown">نامشخص</option>
                </Select>

                <Tooltip label="تازه‌سازی لیست" hasArrow placement="top">
                  <IconButton aria-label="Refresh" icon={<FaSync />} size="md" borderRadius="xl" onClick={() => refetch()} />
                </Tooltip>
              </Flex>
            </Box>

            {/* جدول */}
            <Box overflowX="auto" className="custom-scrollbar">
              <Table variant="simple" size="md">
                <Thead bg={theadBg}>
                  <Tr>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>جزئیات ثبت</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>شماره سفارش (SKU)</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>منبع و درگاه</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>آیتم‌ها / مبلغ</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>وضعیت پردازش</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>همگام‌سازی موجودی</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor} textAlign="center">عملیات</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {orders.length === 0 ? (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={16} borderBottom="none">
                        <Flex direction="column" align="center" justify="center">
                          <Icon as={FaBoxOpen} boxSize={12} color="gray.300" mb={4} />
                          <Text fontSize="lg" fontWeight="bold" color={textColor} mb={1}>سفارشی یافت نشد</Text>
                          <Text fontSize="sm" color={mutedColor}>با تغییر فیلترها دوباره تلاش کنید.</Text>
                        </Flex>
                      </Td>
                    </Tr>
                  ) : (
                    orders.map((o) => {
                      const { itemsCount, totalQty } = orderItemsSummary(o);
                      const sourceColor = SOURCE_COLORS[o.source] || "gray";
                      return (
                        <Tr key={o._id} _hover={{ bg: hoverBg }} transition="background 0.2s">
                          <Td borderColor={borderColor}>
                            <Text fontSize="sm" fontWeight="bold" color={textColor}>{formatDate(o.createdAt).split(' ')[0]}</Text>
                            <Text fontSize="xs" color={mutedColor}>{formatDate(o.createdAt).split(' ').slice(1).join(' ')}</Text>
                          </Td>
                          <Td borderColor={borderColor}>
                            <HStack spacing={2}>
                              <Text fontSize="sm" fontWeight="black" color={textColor}>{o.sku || "—"}</Text>
                              {o.sku && (
                                <IconButton size="xs" aria-label="Copy" icon={<CopyIcon />} variant="ghost" colorScheme="gray" onClick={() => copyToClipboard(o.sku)} />
                              )}
                            </HStack>
                          </Td>
                          <Td borderColor={borderColor}>
                            <VStack align="start" spacing={1}>
                              <Badge colorScheme={sourceColor} fontSize="2xs" px={2} rounded="md">
                                {SOURCE_LABELS[o.source] || o.source || "-"}
                              </Badge>
                              {o.gateway?.name && (
                                <Text fontSize="xs" color={mutedColor} display="flex" alignItems="center" gap={1}>
                                  <Icon as={FaStore} /> {o.gateway.name}
                                </Text>
                              )}
                            </VStack>
                          </Td>
                          <Td borderColor={borderColor}>
                            <VStack align="start" spacing={1}>
                              <Text fontSize="sm" fontWeight="bold" color={textColor}>{totalQty} عدد <Text as="span" fontSize="xs" fontWeight="normal" color={mutedColor}>({itemsCount} کالا)</Text></Text>
                              <Text fontSize="xs" color={mutedColor}>{o.amount != null && o.amount > 0 ? `${o.amount.toLocaleString("fa-IR")} تومان` : "مبلغ نامشخص"}</Text>
                            </VStack>
                          </Td>
                          <Td borderColor={borderColor}>{statusBadge(o.status)}</Td>
                          <Td borderColor={borderColor}>{syncBadge(o.stockSync)}</Td>
                          <Td borderColor={borderColor} textAlign="center">
                            <Tooltip label="مشاهده جزئیات دقیق" hasArrow placement="top">
                              <Button size="sm" colorScheme="blue" variant="light" bg="blue.50" color="blue.600" _hover={{ bg: "blue.100" }} onClick={() => handleViewDetail(o._id)}>
                                <FaEye />
                              </Button>
                            </Tooltip>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </Box>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <Flex justify="center" align="center" p={4} borderTopWidth="1px" borderColor={borderColor} gap={2} bg={paginationBg}>
                <Button size="sm" borderRadius="lg" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>اولین</Button>
                <Button size="sm" borderRadius="lg" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} isDisabled={currentPage === 1}>قبلی</Button>
                <Badge px={3} py={1} borderRadius="lg" colorScheme="blue" variant="subtle">صفحه {pagination.page} از {pagination.pages}</Badge>
                <Button size="sm" borderRadius="lg" onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))} isDisabled={currentPage === pagination.pages}>بعدی</Button>
                <Button size="sm" borderRadius="lg" onClick={() => setCurrentPage(pagination.pages)} isDisabled={currentPage === pagination.pages}>آخرین</Button>
              </Flex>
            )}
          </Box>
        </Box>
      </Flex>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
      `}</style>

      {/* ========================================== */}
      {/* Modal جزئیات سفارش */}
      {/* ========================================== */}
      <CustomModal isOpen={isOpen} onClose={handleClose} title={`جزئیات سفارش ${selectedOrder?.sku || ''}`} size="2xl">
        {detailLoading ? (
          <Flex justify="center" align="center" minH="200px">
            <Spinner size="lg" color="blue.500" />
            <Text mr={4} fontWeight="bold" color={mutedColor}>در حال دریافت جزئیات...</Text>
          </Flex>
        ) : selectedOrder ? (
          <VStack align="stretch" spacing={6} pb={2}>
            
            {/* هدر جزئیات */}
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} p={4} bg={modalHeaderBg} borderRadius="xl">
              <Box>
                <Text fontSize="xs" color={mutedColor}>تاریخ ثبت</Text>
                <Text fontSize="sm" fontWeight="bold" color={textColor}>{formatDate(selectedOrder.createdAt)}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color={mutedColor}>منبع و درگاه</Text>
                <Text fontSize="sm" fontWeight="bold" color={textColor}>
                  {SOURCE_LABELS[selectedOrder.source] || selectedOrder.source} 
                  {selectedOrder.gateway?.name ? ` (${selectedOrder.gateway.name})` : ''}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color={mutedColor}>شماره سفارش</Text>
                <HStack>
                  <Text fontSize="sm" fontWeight="bold" color={textColor} dir="ltr">{selectedOrder.sku}</Text>
                  <IconButton size="xs" icon={<CopyIcon />} variant="ghost" onClick={() => copyToClipboard(selectedOrder.sku)} aria-label="Copy" />
                </HStack>
              </Box>
              <Box>
                <Text fontSize="xs" color={mutedColor}>رهگیری سیستمی</Text>
                {selectedOrder.webhookLogs?.length > 0 ? (
                  <Button as="a" href="/webhook-logs" target="_self" size="xs" colorScheme="blue" variant="link" rightIcon={<ExternalLinkIcon />}>
                    بررسی لاگ‌های وب‌هوک
                  </Button>
                ) : (
                  <Text fontSize="sm" color={textColor}>بدون لاگ</Text>
                )}
              </Box>
            </SimpleGrid>

            {/* جدول آیتم‌های سفارش */}
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={3} color={textColor}>اقلام سفارش:</Text>
              <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" overflow="hidden">
                <Table size="sm" variant="simple">
                  <Thead bg={theadBg}>
                    <Tr>
                      <Th borderColor={borderColor}>کد کالا / SKU</Th>
                      <Th borderColor={borderColor}>نام محصول (در سیستم)</Th>
                      <Th borderColor={borderColor} isNumeric>تعداد</Th>
                      <Th borderColor={borderColor} isNumeric>مبلغ واحد</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(selectedOrder.items || []).map((it, idx) => (
                      <Tr key={idx}>
                        <Td borderColor={borderColor}><Text fontSize="xs" fontWeight="bold" dir="ltr">{it.sku || '—'}</Text></Td>
                        <Td borderColor={borderColor}><Text fontSize="xs" color={mutedColor} isTruncated maxW="150px" title={it.product?.name}>{it.product?.name || 'محصول نامشخص / حذف شده'}</Text></Td>
                        <Td borderColor={borderColor} isNumeric><Badge colorScheme="blue" rounded="md" px={2}>{it.quantity}</Badge></Td>
                        <Td borderColor={borderColor} isNumeric><Text fontSize="xs">{it.unitPrice != null && it.unitPrice > 0 ? it.unitPrice.toLocaleString('fa-IR') : '—'}</Text></Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>

            {/* وضعیت همگام‌سازی */}
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={3} color={textColor}>وضعیت کسر و همگام‌سازی موجودی:</Text>
              {(() => {
                const state = syncState(selectedOrder.stockSync);
                const look = {
                  ok: { bg: stockSyncOkBg, icon: FaSync, c: "green", title: "عملیات موفق" },
                  failed: { bg: stockSyncFailBg, icon: FaExclamationTriangle, c: "red", title: "عملیات با خطا مواجه شد" },
                  unknown: { bg: stockSyncUnknownBg, icon: FaSync, c: "gray", title: "نامشخص — همگام‌سازی در صف انجام می‌شود" },
                }[state];
                return (
                  <Flex p={4} bg={look.bg} borderRadius="xl" align="center" gap={3}>
                    <Icon as={look.icon} color={`${look.c}.500`} boxSize={5} />
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color={`${look.c}.700`}>
                        {look.title}
                      </Text>
                      <Text fontSize="xs" color={`${look.c}.600`} mt={1}>
                        {state === "unknown"
                          ? "نتیجهٔ ارسال به درگاه‌ها روی سفارش ثبت نمی‌شود؛ گزارش همگام‌سازی موجودی را ببینید"
                          : `زمان آخرین تلاش: ${formatDate(selectedOrder.stockSync?.attemptedAt)}`}
                      </Text>
                    </Box>
                  </Flex>
                );
              })()}

              {detailFailures.length > 0 && (
                <Stack spacing={2} mt={3}>
                  {detailFailures.map((f, idx) => (
                    <Box
                      key={idx}
                      p={3}
                      borderWidth="1px"
                      borderColor="red.200"
                      bg={failureBoxBg}
                      borderRadius="lg"
                      borderRightWidth="4px"
                      borderRightColor="red.400"
                    >
                      <Text fontSize="xs" fontWeight="bold" color="red.600" mb={1}>
                        درگاه: {f.gatewayName || "نامشخص"} {f.gatewayType ? `(${f.gatewayType})` : ""}
                      </Text>
                      <Text fontSize="xs" color={textColor}>{f.message || "علت خطا نامشخص است."}</Text>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            {/* خطای اصلی سفارش (در صورت وجود) */}
            {selectedOrder.lastError && (
              <Alert status="error" borderRadius="xl">
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize="sm">خطای بحرانی در ثبت سفارش</AlertTitle>
                  <AlertDescription fontSize="xs" mt={1}>{selectedOrder.lastError}</AlertDescription>
                </Box>
              </Alert>
            )}
          </VStack>
        ) : (
          <Text color="gray.500" py={6} textAlign="center">جزئیاتی برای این سفارش یافت نشد.</Text>
        )}
      </CustomModal>

      {/* ========================================== */}
      {/* Modal ثبت سفارش دستی */}
      {/* ========================================== */}
      <CustomModal isOpen={isManualOpen} onClose={onManualClose} title="ثبت سفارش دستی جدید" size="3xl">
        <ManualOrderForm
          onSuccess={() => {
            onManualClose();
            queryClient.invalidateQueries(["orders"]);
            queryClient.invalidateQueries(["products"]);
          }}
        />
      </CustomModal>
    </>
  );
}